/**
 * @fileoverview Edit Prospect dialog for Sales Pipeline (PATCH /sales/prospects/{id}).
 * @description Loads the prospect's details (with the version tag, ETag) and the catalogue choices,
 * shows the five sections filled with the current values (EditProspectDetailsFields) and saves them
 * against the version shown. A change made by someone else since (a refetch that returns a new
 * ETag, or a `412 / 4105` refusal) shows the stale-version notice and pauses saving; what was typed
 * stays in the form, and only the person's Refresh loads the latest values over it. The bank's registration number
 * and type cannot change, a prospect with an open target keeps its lead rating (`409 / 4112`), and
 * editing a pending submission sends it for review again; the backend decides each and any refusal
 * shows as the dialog's error. Close is held while the save is in flight, and the confirmation
 * runs from the mutation itself, so it survives the dialog unmounting. The parent mounts it only
 * while it's open.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useEditProspect } from '@/features/sales/hooks/useEditProspect'
import { useProspectDetails } from '@/features/sales/hooks/useProspectDetails'
import { useProspectMetadata } from '@/features/sales/hooks/useProspectMetadata'
import { useRefreshSalesQueries } from '@/features/sales/hooks/useRefreshSalesQueries'
import {
  editValuesFromDetails,
  tierNameFor,
  toEditProspectRequest,
} from '@/features/sales/lib/prospect-form.utils'
import { isStaleVersionError } from '@/features/sales/lib/stale-version.utils'
import {
  editProspectDetailsFormSchema,
  type EditProspectDetailsFormValues,
} from '@/features/sales/schemas/edit-prospect-details-form.schema'
import Modal from '@/shared/components/molecules/Modal'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import DialogActions from './DialogActions'
import EditProspectDetailsFields from './EditProspectDetailsFields'
import SalesRequestError from './SalesRequestError'
import StaleVersionNotice from './StaleVersionNotice'

import type {
  ProspectDetails,
  ProspectEdited,
  ProspectListItem,
  ProspectMetadata,
  VersionedProspectDetails,
} from '@/features/sales/types/prospect.types'
import type { UseQueryResult } from '@tanstack/react-query'
import type { FormEvent } from 'react'

interface EditProspectDialogProps {
  prospect: ProspectListItem
  onClose: () => void
  onEdited: (result: ProspectEdited, prospect: ProspectListItem) => void
}

/** The prospect's details as the form was opened (or last refreshed) with, and their version. */
interface LoadedProspect {
  details: ProspectDetails
  etag: string
}

interface EditProspectFormProps {
  details: ProspectDetails
  etag: string
  metadata: ProspectMetadata
  edit: ReturnType<typeof useEditProspect>
  onClose: () => void
  onRefresh: () => void
  isRefreshing: boolean
  /** Whether a refetch has returned a newer version than the form was opened with. */
  changedSince: boolean
  prospect: ProspectListItem
}

/**
 * @component
 * @param {EditProspectFormProps} props - The loaded prospect, its version and the handlers.
 * @param {ProspectListItem} props.prospect - The row's prospect.
 * @param {ProspectDetails} props.details - The loaded details the form starts from.
 * @param {string} props.etag - The version of those details, sent as If-Match.
 * @param {ProspectMetadata} props.metadata - The catalogue choices.
 * @param {ReturnType<typeof useEditProspect>} props.edit - The dialog's save mutation.
 * @param {() => void} props.onClose - Dismisses the dialog (held while the save is in flight).
 * @param {() => void} props.onRefresh - Loads the latest version over what was typed.
 * @param {boolean} props.isRefreshing - Whether that reload is running.
 * @param {boolean} props.changedSince - Whether a newer version than this one has been fetched.
 */
function EditProspectForm({
  prospect,
  details,
  etag,
  metadata,
  edit,
  onClose,
  onRefresh,
  isRefreshing,
  changedSince,
}: EditProspectFormProps) {
  const { t } = useTranslation()
  const refused = edit.isError && isStaleVersionError(edit.error)
  const isStale = refused || changedSince
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EditProspectDetailsFormValues>({
    resolver: zodResolver(editProspectDetailsFormSchema),
    defaultValues: editValuesFromDetails(details),
  })
  const branches = useWatch({ control, name: 'branches' })

  /**
   * @description Saves the validated values against the version shown; the dialog's mutation
   * reports the result.
   * @param {EditProspectDetailsFormValues} values - The form's values.
   */
  const onValid = (values: EditProspectDetailsFormValues) => {
    edit.mutate({ prospectId: prospect.prospectId, etag, request: toEditProspectRequest(values) })
  }

  /**
   * @description Validates and submits the form.
   * @param {FormEvent<HTMLFormElement>} event - The submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  /**
   * @description Clears a stale refusal and loads the prospect's latest values over the form.
   */
  const refresh = () => {
    edit.reset()
    onRefresh()
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      data-testid="edit-prospect-form"
    >
      {details.admissionState === 'PENDING_REVIEW' && (
        <p
          className="col-span-full rounded-lg border border-selection-border bg-selection-bg px-3.5 py-2.5 text-[12.5px] text-info"
          data-testid="edit-prospect-pending-hint"
        >
          {t('sales.pipeline.edit.pendingHint')}
        </p>
      )}
      <EditProspectDetailsFields
        register={register}
        errors={errors}
        metadata={metadata}
        previewTier={tierNameFor(metadata.tierScheme.ranges, branches || 0)}
      />
      <div className="col-span-full flex flex-col gap-3">
        {isStale && (
          <StaleVersionNotice
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            message={refused ? undefined : t('sales.pipeline.edit.changedSince')}
          />
        )}
        {edit.isError && !refused && <SalesRequestError error={edit.error} />}
        <DialogActions
          testIdPrefix="edit-prospect"
          cancelLabel={t('sales.pipeline.form.cancel')}
          confirmLabel={t('sales.pipeline.edit.submit')}
          onCancel={onClose}
          confirmType="submit"
          isLoading={edit.isPending}
          disabled={isStale}
        />
      </div>
    </form>
  )
}

/**
 * @description Holds the version the form was opened with, so a refetch that returns a new ETag
 * never resets what was typed. The first version loaded is kept; only the person's Refresh adopts
 * the latest one, once its reload has finished (adjusting state while rendering, not in an effect).
 * @param {UseQueryResult<VersionedProspectDetails>} details - The prospect's details query.
 * @returns {object} The version the form uses, whether a newer one exists, and how to adopt it.
 */
function useFormBaseline(details: UseQueryResult<VersionedProspectDetails>) {
  const [loaded, setLoaded] = useState<LoadedProspect | null>(null)
  const [reloading, setReloading] = useState(false)
  const latest: LoadedProspect | null = details.isSuccess
    ? { details: details.data.prospect, etag: details.data.etag }
    : null

  if (!loaded && latest) setLoaded(latest)

  /**
   * @description Fetches the latest version and makes the form use it.
   */
  const reload = async () => {
    setReloading(true)
    const result = await details.refetch()
    if (result.data) setLoaded({ details: result.data.prospect, etag: result.data.etag })
    setReloading(false)
  }

  return {
    loaded,
    reloading,
    changedSince: loaded !== null && latest !== null && latest.etag !== loaded.etag,
    reload,
  }
}

/**
 * @component
 * @param {EditProspectDialogProps} props - The prospect and the handlers.
 * @param {ProspectListItem} props.prospect - The prospect to edit.
 * @param {() => void} props.onClose - Called when the dialog is dismissed.
 * @param {(result: ProspectEdited, prospect: ProspectListItem) => void} props.onEdited - Called
 * once the prospect is saved.
 */
function EditProspectDialog({ prospect, onClose, onEdited }: EditProspectDialogProps) {
  const { t } = useTranslation()
  const details = useProspectDetails(prospect.prospectId)
  const metadataQuery = useProspectMetadata()
  const refreshSales = useRefreshSalesQueries()
  const edit = useEditProspect({
    /**
     * @description Reports the saved edit to the page, even if this dialog has unmounted.
     * @param {ProspectEdited} result - What the edit answered.
     */
    onSuccess: (result) => onEdited(result, prospect),
  })
  const close = useGuardedClose(onClose, edit.isPending)
  const baseline = useFormBaseline(details)
  const { loaded } = baseline

  /**
   * @description Refreshes the sales lists and loads the latest version over the form's values.
   */
  const refresh = () => {
    refreshSales()
    void baseline.reload()
  }

  return (
    <Modal
      open
      onClose={close}
      data-testid="edit-prospect-dialog"
      title={t('sales.pipeline.edit.title')}
      subtitle={prospect.bankName}
      size="lg"
    >
      {loaded && metadataQuery.isSuccess ? (
        <EditProspectForm
          key={loaded.etag}
          prospect={prospect}
          details={loaded.details}
          etag={loaded.etag}
          metadata={metadataQuery.data}
          edit={edit}
          onClose={close}
          onRefresh={refresh}
          isRefreshing={baseline.reloading}
          changedSince={baseline.changedSince}
        />
      ) : details.isError || metadataQuery.isError ? (
        <p role="alert" className="text-[13px] text-danger" data-testid="edit-prospect-load-error">
          {t('sales.pipeline.edit.loadError')}
        </p>
      ) : (
        <p className="text-[13px] text-slate" data-testid="edit-prospect-loading">
          {t('sales.pipeline.edit.loading')}
        </p>
      )}
    </Modal>
  )
}

export default EditProspectDialog
